import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { UsersStore } from '@frontend-site/shared/core';
import { PFieldAbstractComponent } from '../p-field-abstract';
import { MatMenuTrigger } from '@angular/material/menu';
import { TranslateService } from '@ngx-translate/core';
import { KeyboardService } from '../../keyboard';
import { ListItem } from '../p-field-single-select';
import { resolveUnconnectedUserDisplay, textOnlyDisplay, UnconnectedUsers, UserDisplay } from '../unconnected-users/unconnected-user-display';

const GROUP_ICON = 'user-groups';

@Component({
    selector: 'p-field-user-and-group',
    styleUrls: ['./p-field-user-and-group.component.scss', '../p-field-single-select/p-field-single-select.component.scss'],
    template: ` <p-field
        [style]="dynamicStyle"
        [isMandatory]="isMandatory"
        [label]="label"
        [labelTooltip]="labelTooltip"
        [isReadOnly]="isReadOnly"
        [shouldTransformLabelTextCase]="shouldTransformLabelTextCase"
        *ngIf="{ disconnectedUsers: disconnectedUsers$ | async, inactiveUsers: inactiveUsers$ | async } as unconnectedUsers"
    >
        <div slot="readMode">
            <mat-form-field class="p-input-text" appearance="fill" [class.p-input-text-readonly]="isReadOnly">
                <p-icon
                    class="p-input-link-prefix"
                    matPrefix
                    *ngIf="getDisplayIcon(value, unconnectedUsers) as icon"
                    [name]="icon"
                    size="sm"
                    [matTooltip]="getDisplayTooltip(value, unconnectedUsers)"
                ></p-icon>
                <input
                    [attr.automation-id]="'p-field-user-and-group_' + label"
                    matInput
                    readonly
                    type="text"
                    class="p-input-text-input"
                    [placeholder]="placeholder | sentenceCase"
                    [value]="getDisplayValue(value, unconnectedUsers)"
                    [disabled]="isDisabled"
                    [matTooltip]="getDisplayValue(value, unconnectedUsers)"
                    [matTooltipDisabled]="isDisabled"
                />
            </mat-form-field>
        </div>
        <div slot="editMode">
            <mat-form-field class="p-input-text" appearance="fill" [mat-menu-trigger-for]="optionsMenu">
                <p-icon
                    class="p-input-link-prefix"
                    matPrefix
                    *ngIf="getDisplayIcon(value, unconnectedUsers) as icon"
                    [name]="icon"
                    size="sm"
                    [matTooltip]="getDisplayTooltip(value, unconnectedUsers)"
                ></p-icon>
                <input
                    #inputField
                    [attr.automation-id]="'p-field-user-and-group_' + label"
                    matInput
                    readonly
                    type="text"
                    class="p-input-text-input"
                    [placeholder]="placeholder | sentenceCase"
                    [value]="getDisplayValue(value, unconnectedUsers)"
                    [matTooltip]="getDisplayValue(value, unconnectedUsers)"
                    (keydown)="onKeydownEvent($event)"
                />
                <p-icon class="p-field-input-icon" iconName="chevron-down" size="sm"></p-icon>
                <mat-menu
                    #optionsMenu="matMenu"
                    (click)="$event.stopPropagation()"
                    [class]="menuCustomClasses + ' no-padding embedded p-menu'"
                >
                    <ng-template matMenuContent>
                        <div fxLayout="column" (click)="$event.stopImmediatePropagation()">
                            <p-picker-single-select
                                [style]="menuDynamicStyle"
                                [emptyOption]="emptyOption"
                                [autoFocus]="true"
                                [list]="mergedList"
                                idField="key"
                                [nameField]="nameField"
                                groupField="section"
                                [value]="getSelectedKey(value)"
                                (valueChange)="changeValue($event)"
                                [canSearch]="canSearch"
                                [noData]="noData"
                                placeholder="search.."
                                customClasses="no-padding"
                            >
                            </p-picker-single-select>
                        </div>
                    </ng-template>
                </mat-menu>
            </mat-form-field>
        </div>
    </p-field>`,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: PFieldUserAndGroupComponent
        }
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'pFieldUserAndGroup'
})
export class PFieldUserAndGroupComponent extends PFieldAbstractComponent<UserOrGroupValue> implements OnChanges {
    @Input() label?: string;
    @Input() isMandatory = false;
    @Input() override isReadOnly = false;
    @Input() canSearch = false;
    @Input() users: ListItem[] = [];
    @Input() groups: ListItem[] = [];
    @Input() menuCustomClasses?: string;
    @Input() noData = this.translate.instant('UserManagement.UserAvatar.noData');
    @Input() placeholder?: string;
    @Input() idField = 'id';
    @Input() nameField = 'name';
    @Input() emptyOption: any = { key: 0, name: this.translate.instant('UserManagement.UserAvatar.emptyOption') };
    @Input() autoOpen = false;
    @Input() public dynamicStyle: { [klass: string]: any } | null | undefined = undefined;
    @Input() public menuDynamicStyle: { [klass: string]: any } | null | undefined = undefined;

    @ViewChild(MatMenuTrigger) matSelect: MatMenuTrigger = {} as MatMenuTrigger;

    protected disconnectedUsers$ = this.usersStore.disconnectedUsers$;
    protected inactiveUsers$ = this.usersStore.inactiveUsers$;
    protected mergedList: MergedItem[] = [];

    constructor(
        public usersStore: UsersStore,
        cd: ChangeDetectorRef,
        private translate: TranslateService,
        private keyboardService: KeyboardService
    ) {
        super(cd);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if ('users' in changes || 'groups' in changes) {
            this.mergedList = this.buildMergedList();
        }
    }

    private buildMergedList(): MergedItem[] {
        const groupsSection = this.translate.instant('PFieldUserAndGroup.groupsSectionTitle');
        const usersSection = this.translate.instant('PFieldUserAndGroup.usersSectionTitle');
        const groupItems: MergedItem[] = (this.groups ?? []).map((g: any) => ({
            ...g,
            key: 'group:' + g[this.idField],
            type: 'group',
            section: groupsSection,
            leftIcon: { name: GROUP_ICON, showOnlyOnHover: false }
        }));
        const userItems: MergedItem[] = (this.users ?? []).map((u: any) => ({
            ...u,
            key: 'user:' + u[this.idField],
            type: 'user',
            section: usersSection
        }));
        return [...groupItems, ...userItems];
    }

    protected getSelectedKey(value?: UserOrGroupValue): string | number {
        if (!value || value.id == null) {
            return this.emptyOption?.key;
        }
        return value.type + ':' + value.id;
    }

    protected changeValue(key: string | number): void {
        this.markAsTouched();
        if (this.isReadOnly) {
            return;
        }
        if (key === this.emptyOption?.key) {
            this.value = undefined;
            this._onChange(this.value);
            this.matSelect?.closeMenu();
            return;
        }
        const item: any = this.mergedList.find((i) => i.key === key);
        if (item) {
            this.value = { id: item[this.idField], name: item[this.nameField], type: item.type };
            this._onChange(this.value);
        }
        this.matSelect?.closeMenu();
    }

    getDisplayValue(value: UserOrGroupValue | undefined, unconnectedUsers: UnconnectedUsers): string {
        return this.resolveDisplay(value, unconnectedUsers).text;
    }

    protected getDisplayIcon(value: UserOrGroupValue | undefined, unconnectedUsers: UnconnectedUsers): string | null {
        return this.resolveDisplay(value, unconnectedUsers).icon;
    }

    protected getDisplayTooltip(value: UserOrGroupValue | undefined, unconnectedUsers: UnconnectedUsers): string {
        return this.resolveDisplay(value, unconnectedUsers).tooltip;
    }

    /**
     * Pure derivation of what the field shows for `value` (text + prefix icon + tooltip).
     * It must stay free of side effects: it is evaluated from template bindings, and the icon's `*ngIf` and
     * `[name]` both read it, so binding order can never hand a stale/null icon name to `p-icon` (IN-01004).
     */
    private resolveDisplay(value: UserOrGroupValue | undefined, unconnectedUsers: UnconnectedUsers): UserDisplay {
        if (!value || value.id == null) {
            return textOnlyDisplay(this.isReadOnly ? '' : this.emptyOption?.name ?? '');
        }
        if (value.type === 'group') {
            const group: any = this.groups.find((g: any) => g[this.idField] === value.id);
            return { text: group?.[this.nameField] ?? value.name, icon: GROUP_ICON, tooltip: '' };
        }
        const user: any = this.users.find((u: any) => u[this.idField] === value.id);
        if (user) {
            return textOnlyDisplay(user[this.nameField]);
        }
        return resolveUnconnectedUserDisplay(value.id, unconnectedUsers, this.translate) ?? textOnlyDisplay(value.name);
    }

    // tslint:disable-next-line:use-lifecycle-interface
    ngAfterViewInit(): void {
        if (this.autoOpen) {
            this.matSelect?.openMenu();
        }
    }

    protected onKeydownEvent(e: KeyboardEvent): void {
        const code = e.keyCode;
        if (this.keyboardService.isDown(code) || this.keyboardService.isEnter(code)) {
            this.matSelect?.openMenu();
        }
    }
}

interface MergedItem extends ListItem {
    key: string;
    type: 'user' | 'group';
    section: string;
}

export interface UserOrGroupValue {
    id: number | string;
    name: string;
    type: 'user' | 'group';
}
